import TextField from '@app/src/components/Ui/TextField'
import type { Unit } from '@app/src/types/resourceExplorer'
import { parseCurrencyInput, toExactCurrencyString } from '@app/src/utils/currencyUtils'
import { InputAdornment, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

type RangeBodyProps = {
  value: unknown
  onChange: (value: [string, string]) => void
  currencyUnit?: Unit
  disabled?: boolean
}

const asRange = (value: unknown): [string?, string?] => (Array.isArray(value) ? (value as [string?, string?]) : [])

/**
 * The min/max pair behind every range filter. With a currency it accepts shorthand ('200k', '1.5M') and
 * echoes the parsed amount underneath, so what was typed and what will be filtered on stay visible.
 */
const RangeBody: React.FC<RangeBodyProps> = ({ value, onChange, currencyUnit, disabled }) => {
  const { formatMessage } = useIntl()
  const [min, max] = asRange(value)

  const renderPreview = (raw?: string): React.ReactNode => {
    if (!currencyUnit || !raw) return null
    const parsed = parseCurrencyInput(raw)
    if (parsed === undefined) return null
    return (
      <Typography variant="caption" color="text.secondary">
        {`= ${toExactCurrencyString(parsed, currencyUnit)}`}
      </Typography>
    )
  }

  const inputProps = currencyUnit?.symbol
    ? { startAdornment: <InputAdornment position="start">{currencyUnit.symbol}</InputAdornment> }
    : undefined

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Stack spacing={0.5}>
        <TextField
          variant="outlined"
          size="small"
          type={currencyUnit ? 'text' : 'number'}
          disabled={disabled}
          label={formatMessage({ id: 'general.min' })}
          value={min ?? ''}
          onChange={event => onChange([event.target.value, max ?? ''])}
          InputProps={inputProps}
        />
        {renderPreview(min)}
      </Stack>
      <Stack spacing={0.5}>
        <TextField
          variant="outlined"
          size="small"
          type={currencyUnit ? 'text' : 'number'}
          disabled={disabled}
          label={formatMessage({ id: 'general.max' })}
          value={max ?? ''}
          onChange={event => onChange([min ?? '', event.target.value])}
          InputProps={inputProps}
        />
        {renderPreview(max)}
      </Stack>
    </Stack>
  )
}

export default RangeBody
